import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { FileText, CalendarDays, Receipt, MapPin, Clock, ArrowRight } from "lucide-react";
import api from "../../lib/api";
import { StatusMark } from "../../components/StatusMark";
import { formatDate, formatTime, currency } from "../../lib/format";

export default function PortalDashboard() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/portal/dashboard").then((r) => setData(r.data)).catch(() => {});
  }, []);

  if (!data) return <p className="font-sans text-sm text-stone">Loading your dashboard…</p>;

  const first = data.student?.first_name || "there";
  const nc = data.next_class;

  return (
    <div className="space-y-10" data-testid="portal-dashboard">
      <div>
        <p className="overline mb-2">Student Portal</p>
        <h1 className="font-serif text-4xl sm:text-5xl text-ink tracking-tight">Hello, {first}.</h1>
        <p className="font-sans text-sm text-stone mt-2">Here's what's on your call sheet.</p>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Next class — call sheet card */}
        <div className="lg:col-span-2 paper-card p-8" data-testid="next-class-card">
          <div className="flex items-center justify-between mb-5">
            <p className="overline">Your next session</p>
            {nc && <StatusMark status={nc.my_status} />}
          </div>
          {nc ? (
            <>
              <h2 className="font-serif text-3xl text-ink leading-tight mb-4">{nc.title}</h2>
              <div className="grid sm:grid-cols-3 gap-4 font-mono text-sm border-t border-hairline pt-5">
                <div>
                  <p className="text-[11px] uppercase tracking-wider text-stone mb-1">Date</p>
                  <p className="text-ink flex items-center gap-1.5">
                    <CalendarDays size={14} /> {formatDate(nc.date)}
                  </p>
                </div>
                <div>
                  <p className="text-[11px] uppercase tracking-wider text-stone mb-1">Call</p>
                  <p className="text-ink flex items-center gap-1.5">
                    <Clock size={14} /> {formatTime(nc.start_time)}
                  </p>
                </div>
                <div>
                  <p className="text-[11px] uppercase tracking-wider text-stone mb-1">
                    {nc.my_status === "approved" ? "Cast #" : "Status"}
                  </p>
                  <p className="text-ink">
                    {nc.my_status === "approved" ? `#${nc.seat_number}` : "Awaiting approval"}
                  </p>
                </div>
              </div>
              <p className="font-sans text-sm text-stone flex items-center gap-1.5 mt-4">
                <MapPin size={14} /> {nc.venue}
              </p>
            </>
          ) : (
            <div>
              <p className="font-sans text-sm text-ink/75 mb-4">
                You're not on a class yet. Browse what's open and request a spot.
              </p>
              <Link to="/portal/classes" className="btn-brass text-sm">
                Find a class <ArrowRight size={15} />
              </Link>
            </div>
          )}
        </div>

        {/* Quick stats */}
        <div className="space-y-4">
          <Link to="/portal/sides" className="block paper-card p-6 hover:border-brass transition-colors" data-testid="quick-sides">
            <div className="flex items-center justify-between">
              <FileText size={20} className="text-brass" />
              <span className="font-mono text-2xl text-ink">{data.sides_count}</span>
            </div>
            <p className="font-serif text-xl text-ink mt-3">My Sides</p>
            <p className="font-sans text-xs text-stone">{data.public_sides_count} more in the library</p>
          </Link>

          <Link to="/portal/billing" className="block paper-card p-6 hover:border-brass transition-colors" data-testid="quick-billing">
            <div className="flex items-center justify-between">
              <Receipt size={20} className="text-brass" />
              <span className="font-mono text-2xl text-ink">{currency(data.amount_due)}</span>
            </div>
            <p className="font-serif text-xl text-ink mt-3">Billing</p>
            <p className="font-sans text-xs text-stone">
              {data.invoices_due > 0 ? `${data.invoices_due} invoice due` : "All settled"}
            </p>
          </Link>
        </div>
      </div>
    </div>
  );
}
